import React, { useEffect, useRef, useCallback } from "react";
import { usePlayer } from "../context/PlayerContext";
import { Play, Pause, Volume2, VolumeX } from "lucide-react";

// Docks the persistent TV <video> into this slot. Does NOT auto-tune —
// listeners keep their radio going until they tap Play Live.
const LiveTvStage = ({ testId = "live-tv-stage" }) => {
  const { current, playing, play, toggle, muted, toggleMute, setTvAnchor, tvStatus, tvError } = usePlayer();
  const slotRef = useRef(null);
  const isTvCurrent = current?.id === "tv";

  const updateAnchor = useCallback(() => {
    if (!slotRef.current) return;
    const r = slotRef.current.getBoundingClientRect();
    setTvAnchor({ top: r.top, left: r.left, width: r.width, height: r.height });
  }, [setTvAnchor]);

  useEffect(() => {
    updateAnchor();
    const ro = new ResizeObserver(updateAnchor);
    if (slotRef.current) ro.observe(slotRef.current);
    window.addEventListener("scroll", updateAnchor, { passive: true });
    window.addEventListener("resize", updateAnchor);
    const id = setInterval(updateAnchor, 500);
    return () => {
      ro.disconnect();
      window.removeEventListener("scroll", updateAnchor);
      window.removeEventListener("resize", updateAnchor);
      clearInterval(id);
      setTvAnchor(null);
    };
  }, [updateAnchor, setTvAnchor]);

  return (
    <div className="relative">
      <div
        ref={slotRef}
        data-testid={testId}
        className="w-full aspect-video rounded-lg border border-gold/40 bg-black relative overflow-hidden"
      >
        {isTvCurrent && tvStatus === "error" && (
          <div className="absolute inset-0 z-20 grid place-items-center bg-black/85 text-center px-6">
            <div>
              <div className="font-condensed uppercase tracking-widest text-crimson-live text-xs mb-1">Stream Error</div>
              <div className="text-neutral-300 text-sm">{tvError || "Cannot play the live TV feed."}</div>
              <button
                data-testid={`${testId}-retry`}
                onClick={() => play("tv")}
                className="mt-4 inline-flex items-center gap-2 bg-gold hover:bg-gold-light text-black font-condensed uppercase tracking-widest text-xs px-4 py-2 rounded-full transition-colors"
              >
                Retry
              </button>
            </div>
          </div>
        )}
        {!isTvCurrent && (
          <div className="absolute inset-0 grid place-items-center bg-[radial-gradient(ellipse_at_center,rgba(212,175,55,0.12),transparent_60%)]">
            <button
              data-testid={`${testId}-start`}
              onClick={() => play("tv")}
              className="inline-flex items-center gap-3 bg-gold hover:bg-gold-light text-black font-condensed uppercase tracking-widest text-xs md:text-sm px-6 py-3.5 rounded-full transition-colors shadow-[0_0_40px_rgba(212,175,55,0.35)]"
            >
              <Play className="w-5 h-5" />
              Watch Live TV
            </button>
          </div>
        )}
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-2 border border-gold/40 rounded-full px-3 py-1.5 bg-black/40">
          <span className="relative flex w-2 h-2">
            <span className="absolute inset-0 rounded-full bg-crimson-live live-ping" />
            <span className="relative inline-flex w-2 h-2 rounded-full bg-crimson-live live-dot" />
          </span>
          <span className="font-condensed uppercase tracking-[0.3em] text-[10px] text-gold">Live · GFN Soul NYC TV</span>
        </span>
        <button
          data-testid={`${testId}-toggle`}
          onClick={() => (isTvCurrent ? toggle() : play("tv"))}
          className="inline-flex items-center gap-2 bg-gold hover:bg-gold-light text-black font-condensed uppercase tracking-widest text-[11px] px-4 py-2 rounded-full transition-colors"
        >
          {isTvCurrent && playing ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
          {isTvCurrent && playing ? "Pause" : "Play Live"}
        </button>
        <button
          data-testid={`${testId}-mute`}
          onClick={toggleMute}
          className="inline-flex items-center gap-2 border border-gold/50 hover:border-gold text-gold font-condensed uppercase tracking-widest text-[11px] px-4 py-2 rounded-full transition-colors"
        >
          {muted ? <VolumeX className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
          {muted ? "Unmute" : "Mute"}
        </button>
      </div>
    </div>
  );
};

export default LiveTvStage;
